import { Accordion } from "@/components/ui/accordion";
import React from "react";
import { useLocale, useTranslations } from "../../../helpers/translations";
import { generateLocalizedPath } from "../helpers/links";
import { MARKETING_ROUTES, RAILS_ROUTES } from "../helpers/routes";
import { MobileNavSubSection } from "../shared/MobileNavSubSection";
import { NavColumnGroup } from "../shared/NavColumnGroup";
import { NavSubColumn } from "../shared/NavSubColumn";
import { NavSubMenuSection } from "../shared/NavSubMenuSection";

type SolutionsSubMenuProps = {
  compact?: boolean;
  wrapperComponent: React.ComponentType<{
    title: string;
    children: React.ReactNode;
  }>;
};

export const SolutionsSubMenu = ({
  compact,
  wrapperComponent,
}: SolutionsSubMenuProps) => {
  const t = useTranslations("nav.application");
  const locale = useLocale();
  const WrapperComponent = wrapperComponent;

  if (!compact) {
    return (
      <WrapperComponent title={t("title")}>
        <NavSubMenuSection
          heading={t("industries.header")}
          items={[
            {
              title: t("industries.links.automotive.title"),
              href: generateLocalizedPath(RAILS_ROUTES.INDUSTRIES_AUTOMOTIVE, locale),
            },
            {
              title: t("industries.links.aerospace.title"),
              href: generateLocalizedPath(RAILS_ROUTES.INDUSTRIES_AEROSPACE_DEFENSE, locale),
            },
            {
              title: t("industries.links.consumer_goods.title"),
              href: generateLocalizedPath(RAILS_ROUTES.INDUSTRIES_CONSUMER_GOODS, locale),
            },
            {
              title: t("industries.links.education.title"),
              href: generateLocalizedPath(RAILS_ROUTES.INDUSTRIES_EDUCATION_RESEARCH, locale),
            },
          ]}
          cta={{
            text: t("industries.cta.text"),
            href: generateLocalizedPath(RAILS_ROUTES.CUSTOMER_STORIES, locale),
          }}
        />
        <NavColumnGroup
          heading={t("content_title")}
          cta={{
            href: generateLocalizedPath(RAILS_ROUTES.APPLICATIONS, locale),
            text: t("view_more.text"),
          }}
        >
          <NavSubColumn
            subtitle={t("col1.header")}
            items={[
              {
                title: t("col1.palletizing.title"),
                description: t("col1.palletizing.description"),
                href: generateLocalizedPath(
                  MARKETING_ROUTES.COBOT_PALLETIZER,
                  locale,
                ),
              },
              {
                title: t("col1.industrial_palletizer.title"),
                href: generateLocalizedPath(
                  RAILS_ROUTES.INDUSTRIAL_PALLETIZER,
                  locale,
                ),
              },
              {
                title: t("col1.sanding.title"),
                description: t("col1.sanding.description"),
                href: generateLocalizedPath(RAILS_ROUTES.ROBOTIC_SANDING, locale),
              },
              {
                title: t("col1.aiOperator.title"),
                description: t("col1.aiOperator.description"),
                href: generateLocalizedPath(
                  MARKETING_ROUTES.RAPID_OPERATOR_AI,
                  locale,
                ),
                badge: t("col1.aiOperator.badge"),
              },
              {
                title: t("col1.EOLSolutions.title"),
                href: generateLocalizedPath(
                  MARKETING_ROUTES.END_OF_LINE_SOLUTIONS,
                  locale,
                ),
              },
            ]}
          />
          <NavSubColumn
            subtitle={t("col2.header")}
            items={[
              {
                title: t("col2.tending.title"),
                description: t("col2.tending.description"),
                href: generateLocalizedPath(RAILS_ROUTES.CNC_MACHINE_TENDING, locale),
              },
              {
                title: t("col2.welding.title"),
                description: t("col2.welding.description"),
                href: generateLocalizedPath(RAILS_ROUTES.ROBOTIC_WELDING, locale),
              },
              {
                title: t("col2.workstations.title"),
                href: generateLocalizedPath(RAILS_ROUTES.WORKSTATIONS, locale),
              },
              {
                title: t("col2.range_extenders.title"),
                href: generateLocalizedPath(RAILS_ROUTES.SEVENTH_AXIS, locale),
              },
              {
                title: t("col3.industrial_carts.title"),
                href: generateLocalizedPath(RAILS_ROUTES.INDUSTRIAL_CARTS, locale),
              },
            ]}
          />
        </NavColumnGroup>
        <NavSubMenuSection
          heading={t("services.header")}
          items={[
            {
              title: t("services.links.design_services.title"),
              href: generateLocalizedPath(
                MARKETING_ROUTES.SERVICES_DESIGN_SERVICES,
                locale,
              ),
            },
            {
              title: t("services.links.deployment_assistance.title"),
              href: generateLocalizedPath(
                MARKETING_ROUTES.SERVICES_DEPLOYMENT_SERVICES,
                locale,
              ),
            },
            {
              title: t("services.links.post_sale_support.title"),
              href: generateLocalizedPath(
                MARKETING_ROUTES.SERVICES_REMOTE_SUPPORT,
                locale,
              ),
            },
            {
              title: t("services.links.professional_services.title"),
              href: generateLocalizedPath(
                MARKETING_ROUTES.SERVICES_TRAINING_SESSIONS,
                locale,
              ),
            },
            {
              title: t("services.links.vention_automation_partners.title"),
              href: generateLocalizedPath(
                RAILS_ROUTES.VENTION_AUTOMATION_PARTNER,
                locale,
              ),
            },
          ]}
          cta={{
            text: t("services.explore_all.text"),
            href: generateLocalizedPath(MARKETING_ROUTES.SERVICES, locale),
          }}
        />
      </WrapperComponent>
    );
  }

  return (
    <WrapperComponent title={t("title")}>
      <Accordion type="multiple" className="w-full border-none">
        <MobileNavSubSection
          title={t("industries.header")}
          items={[
            {
              title: t("industries.links.automotive.title"),
              href: generateLocalizedPath(RAILS_ROUTES.INDUSTRIES_AUTOMOTIVE, locale),
            },
            {
              title: t("industries.links.aerospace.title"),
              href: generateLocalizedPath(RAILS_ROUTES.INDUSTRIES_AEROSPACE_DEFENSE, locale),
            },
            {
              title: t("industries.links.consumer_goods.title"),
              href: generateLocalizedPath(RAILS_ROUTES.INDUSTRIES_CONSUMER_GOODS, locale),
            },
            {
              title: t("industries.links.education.title"),
              href: generateLocalizedPath(RAILS_ROUTES.INDUSTRIES_EDUCATION_RESEARCH, locale),
            },
          ]}
          cta={{
            text: t("industries.cta.text"),
            href: generateLocalizedPath(RAILS_ROUTES.CUSTOMER_STORIES, locale),
          }}
        />
        <MobileNavSubSection title={t("content_title")}>
          <MobileNavSubSection
            title={t("col1.header")}
            titleClassName="text-tertiary hover:text-secondary data-[state=open]:text-primary"
            items={[
              {
                title: t("col1.palletizing.title"),
                description: t("col1.palletizing.description"),
                href: generateLocalizedPath(
                  MARKETING_ROUTES.COBOT_PALLETIZER,
                  locale,
                ),
              },
              {
                title: t("col1.industrial_palletizer.title"),
                href: generateLocalizedPath(
                  RAILS_ROUTES.INDUSTRIAL_PALLETIZER,
                  locale,
                ),
              },
              {
                title: t("col1.sanding.title"),
                description: t("col1.sanding.description"),
                href: generateLocalizedPath(RAILS_ROUTES.ROBOTIC_SANDING, locale),
              },
              {
                title: t("col1.aiOperator.title"),
                description: t("col1.aiOperator.description"),
                href: generateLocalizedPath(
                  MARKETING_ROUTES.RAPID_OPERATOR_AI,
                  locale,
                ),
                badge: t("col1.aiOperator.badge"),
              },
              {
                title: t("col1.EOLSolutions.title"),
                href: generateLocalizedPath(
                  MARKETING_ROUTES.END_OF_LINE_SOLUTIONS,
                  locale,
                ),
              },
            ]}
            cta={{ text: t("view_more.text"), href: generateLocalizedPath(RAILS_ROUTES.APPLICATIONS, locale) }}
          />
          <MobileNavSubSection
            title={t("col2.header")}
            titleClassName="text-tertiary hover:text-secondary data-[state=open]:text-primary"
            items={[
              {
                title: t("col2.tending.title"),
                description: t("col2.tending.description"),
                href: generateLocalizedPath(RAILS_ROUTES.CNC_MACHINE_TENDING, locale),
              },
              {
                title: t("col2.welding.title"),
                description: t("col2.welding.description"),
                href: generateLocalizedPath(RAILS_ROUTES.ROBOTIC_WELDING, locale),
              },
              {
                title: t("col2.workstations.title"),
                href: generateLocalizedPath(RAILS_ROUTES.WORKSTATIONS, locale),
              },
              {
                title: t("col2.range_extenders.title"),
                href: generateLocalizedPath(RAILS_ROUTES.SEVENTH_AXIS, locale),
              },
              {
                title: t("col3.industrial_carts.title"),
                href: generateLocalizedPath(RAILS_ROUTES.INDUSTRIAL_CARTS, locale),
              },
            ]}
            cta={{ text: t("view_more.text"), href: generateLocalizedPath(RAILS_ROUTES.APPLICATIONS, locale) }}
          />
        </MobileNavSubSection>
        <MobileNavSubSection
          title={t("services.header")}
          items={[
            {
              title: t("services.links.design_services.title"),
              href: generateLocalizedPath(
                MARKETING_ROUTES.SERVICES_DESIGN_SERVICES,
                locale,
              ),
            },
            {
              title: t("services.links.deployment_assistance.title"),
              href: generateLocalizedPath(
                MARKETING_ROUTES.SERVICES_DEPLOYMENT_SERVICES,
                locale,
              ),
            },
            {
              title: t("services.links.post_sale_support.title"),
              href: generateLocalizedPath(
                MARKETING_ROUTES.SERVICES_REMOTE_SUPPORT,
                locale,
              ),
            },
            {
              title: t("services.links.professional_services.title"),
              href: generateLocalizedPath(
                MARKETING_ROUTES.SERVICES_TRAINING_SESSIONS,
                locale,
              ),
            },
            {
              title: t("services.links.vention_automation_partners.title"),
              href: generateLocalizedPath(
                RAILS_ROUTES.VENTION_AUTOMATION_PARTNER,
                locale,
              ),
            },
          ]}
          cta={{
            text: t("services.explore_all.text"),
            href: generateLocalizedPath(MARKETING_ROUTES.SERVICES, locale),
          }}
        />
      </Accordion>
    </WrapperComponent>
  );
};
